import {
  Box,
  Button,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { SbomType } from 'common-types';
import pluralize from 'pluralize';
import React, { FC, SyntheticEvent, useCallback } from 'react';
import { BiCodeAlt } from 'react-icons/bi';
import { BsImage } from 'react-icons/bs';
import { FaFileCsv, FaFilePdf, FaHtml5 } from 'react-icons/fa';
import {
  MdAccountCircle as AccountCircleIcon,
  MdClear as ClearIcon,
  MdEdit as Edit,
  MdExpandMore as ExpandMoreIcon,
  MdPolicy,
  MdSearch as Search,
} from 'react-icons/md';
import { VscJson, VscOrganization } from 'react-icons/vsc';
import { makeStyles } from 'tss-react/mui';
import { ReactComponent as PBOMIcon } from '../../../assets/icons/pbom.svg';
import { ReactComponent as SBOMIcon } from '../../../assets/icons/sbom.svg';
import { BirthCertificateResponseType } from '../../../birth-certificate/birth-certificate-types';
import { getCurrentThemeType } from '../../../common/themes';
import { ThemeType } from '../../../connections/types/connection-types';
import ExtendedMenuItem from '../../../elements/OxMenu/ExtendedMenuItem';
import OxMenuControlled from '../../../elements/OxMenu/OxMenuControlled';
import SubMenuItem from '../../../elements/OxMenu/SubMenuItem';
import { useIsScanning } from '../../../new-scan/actions/scan-actions';
import {
  handleDownloadAppSbom,
  handleDownloadOrgSbom,
} from '../../actions/applications-sbom-actions';
import { DownloadType } from '../../applications-types';
import ControlActionButton from './ControlActionButton';

const ApplicationControls: FC<
  React.PropsWithChildren<ApplicationControlsProps>
> = ({
  disabled,
  searchValue,
  ecrConfigured,
  selectedCount,
  searchChange,
  clearSelection,
  handleOwnersClick,
  handleIrrelevantClick,
  handleEditPriorityClick,
  downloadBirthCertificate,
  handlePolicySettingsClick,
  handleOpenMenuSBOM,
  handleCloseMenuSBOM,
  openSBOM,
}) => {
  const { classes } = useStyles();
  const currentThemeType = getCurrentThemeType();
  const handleDownloadAppTypeOrgSbom = useCallback(
    (downloadType: DownloadType, type: SbomType) => {
      handleDownloadAppSbom(downloadType, type);
    },
    [],
  );

  const handleBirthCertificateClick = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      const { key } = event.currentTarget.dataset;
      key && downloadBirthCertificate(key as BirthCertificateResponseType);
    },
    [downloadBirthCertificate],
  );

  const handleDownloadTypeOrgSbom = useCallback((type: DownloadType) => {
    handleDownloadOrgSbom(type);
  }, []);

  const isScanning = useIsScanning();

  return (
    <Box
      display='flex'
      flexDirection='row'
      justifyContent='space-between'
      alignItems='center'>
      <Box display='flex' alignItems='center' flexDirection='row' flexGrow={1}>
        <Box display='flex' flexDirection='row' alignItems='center'>
          <Typography color='text.primary' variant='caption'>
            {selectedCount} {pluralize('applications', selectedCount)} selected
          </Typography>
          <IconButton size='small' color='primary' onClick={clearSelection}>
            <ClearIcon />
          </IconButton>
        </Box>

        <TextField
          size='small'
          value={searchValue}
          onChange={searchChange}
          id='search-apps'
          placeholder='Search'
          sx={{
            minWidth: 200,
            mx: 1,
          }}
          InputProps={{
            endAdornment: <Search />,
          }}
        />
      </Box>
      <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
        <ControlActionButton
          onClick={handlePolicySettingsClick}
          disabled={disabled || isScanning}
          label='Pipeline Settings'
          startIcon={<MdPolicy style={{ width: '1rem' }} />}
        />

        <Tooltip
          arrow
          title={
            !isScanning ? (
              !disabled ? (
                <List>
                  <SubMenuItem
                    disabled={disabled}
                    onClick={handleEditPriorityClick}>
                    Change Business Priority
                  </SubMenuItem>
                  <SubMenuItem
                    disabled={disabled}
                    onClick={handleIrrelevantClick}>
                    Make Irrelevant
                  </SubMenuItem>
                </List>
              ) : (
                <Typography variant='body2' color='text.primary'>
                  Option available when at least one application is selected
                </Typography>
              )
            ) : (
              <Typography variant='body2' color='text.primary'>
                Option available when a scan finishes
              </Typography>
            )
          }>
          <div>
            <ControlActionButton
              disabled={disabled}
              label='Set Priority'
              startIcon={<Edit style={{ width: '1rem' }} />}
              endIcon={<ExpandMoreIcon />}
            />
          </div>
        </Tooltip>

        <OxMenuControlled
          trigger={
            isScanning ? (
              <Tooltip
                title={
                  <Typography variant='body2' color='text.primary'>
                    Option available when a scan finishes
                  </Typography>
                }>
                <Button
                  classes={{
                    startIcon: classes.startIcon,
                  }}
                  startIcon={<SBOMIcon />}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    textTransform: 'none',
                    color:
                      currentThemeType === ThemeType.Dark
                        ? 'rgba(255, 255, 255, 0.3) '
                        : 'rgba(0, 0, 0, 0.26);',
                  }}>
                  <Typography variant='caption' color='inherit'>
                    Download SBOM
                  </Typography>
                </Button>
              </Tooltip>
            ) : (
              <Button
                classes={{
                  startIcon: classes.startIcon,
                }}
                startIcon={<SBOMIcon />}
                endIcon={<ExpandMoreIcon />}
                onClick={handleOpenMenuSBOM}
                sx={{ textTransform: 'none' }}>
                <Typography variant='caption' color='inherit'>
                  Download SBOM
                </Typography>
              </Button>
            )
          }
          open={openSBOM}
          onClose={handleCloseMenuSBOM}>
          <div>
            <ExtendedMenuItem
              id='generated-from-code-menu'
              withIcon
              subMenu={
                <List>
                  <SubMenuItem
                    disabled={disabled}
                    onClick={() => {
                      handleDownloadAppTypeOrgSbom(
                        DownloadType.CSV,
                        SbomType.Repo,
                      );
                      handleCloseMenuSBOM();
                    }}>
                    <ListItemIcon>
                      <FaFileCsv fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>CSV</ListItemText>
                  </SubMenuItem>
                  <SubMenuItem
                    disabled={disabled}
                    onClick={() => {
                      handleDownloadAppTypeOrgSbom(
                        DownloadType.JSON,
                        SbomType.Repo,
                      );
                      handleCloseMenuSBOM();
                    }}>
                    <ListItemIcon>
                      <VscJson fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>CycloneDX JSON</ListItemText>
                  </SubMenuItem>
                </List>
              }>
              <ListItemIcon>
                <BiCodeAlt fontSize='large' />
              </ListItemIcon>
              <ListItemText>Generated From Code</ListItemText>
            </ExtendedMenuItem>
            <ExtendedMenuItem
              id='sbom-app-menu'
              withIcon
              subMenu={
                <List>
                  <SubMenuItem
                    disabled={disabled || ecrConfigured}
                    onClick={() => {
                      handleDownloadAppTypeOrgSbom(
                        DownloadType.CSV,
                        SbomType.Image,
                      );
                      handleCloseMenuSBOM();
                    }}>
                    <ListItemIcon>
                      <FaFileCsv fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>CSV</ListItemText>
                  </SubMenuItem>
                  <SubMenuItem
                    disabled={disabled || ecrConfigured}
                    onClick={() =>
                      handleDownloadAppTypeOrgSbom(
                        DownloadType.JSON,
                        SbomType.Image,
                      )
                    }>
                    <ListItemIcon>
                      <VscJson fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>CycloneDX JSON</ListItemText>
                  </SubMenuItem>
                </List>
              }>
              <ListItemIcon>
                <BsImage fontSize='large' />
              </ListItemIcon>
              <ListItemText> Generated From Image</ListItemText>
            </ExtendedMenuItem>
            <ExtendedMenuItem
              id='sbom-org-menu'
              withIcon
              subMenu={
                <List>
                  <SubMenuItem
                    onClick={() => {
                      handleDownloadTypeOrgSbom(DownloadType.CSV);
                      handleCloseMenuSBOM();
                    }}>
                    <ListItemIcon>
                      <FaFileCsv fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>CSV</ListItemText>
                  </SubMenuItem>
                  <SubMenuItem
                    onClick={() => {
                      handleDownloadTypeOrgSbom(DownloadType.JSON);
                      handleCloseMenuSBOM();
                    }}>
                    <ListItemIcon>
                      <VscJson fontSize='large' />
                    </ListItemIcon>
                    <ListItemText> CycloneDX JSON</ListItemText>
                  </SubMenuItem>
                </List>
              }>
              <ListItemIcon>
                <VscOrganization fontSize='large' />
              </ListItemIcon>
              <ListItemText>For Entire Org</ListItemText>
            </ExtendedMenuItem>
          </div>
        </OxMenuControlled>

        <Tooltip
          arrow
          title={
            !isScanning ? (
              !disabled ? (
                <List>
                  <SubMenuItem
                    onClick={handleBirthCertificateClick}
                    disabled={disabled}
                    data-key={BirthCertificateResponseType.Json}>
                    <ListItemIcon>
                      <VscJson fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>As JSON</ListItemText>
                  </SubMenuItem>
                  <SubMenuItem
                    onClick={handleBirthCertificateClick}
                    disabled={disabled}
                    data-key={BirthCertificateResponseType.Html}>
                    <ListItemIcon>
                      <FaHtml5 fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>As HTML</ListItemText>
                  </SubMenuItem>
                  <SubMenuItem
                    onClick={handleBirthCertificateClick}
                    disabled={disabled}
                    data-key={BirthCertificateResponseType.Pdf}>
                    <ListItemIcon>
                      <FaFilePdf fontSize='large' />
                    </ListItemIcon>
                    <ListItemText>As PDF</ListItemText>
                  </SubMenuItem>
                </List>
              ) : (
                <Typography variant='body2' color='text.primary'>
                  Option available when at least one application is selected
                </Typography>
              )
            ) : (
              <Typography variant='body2' color='text.primary'>
                Option only available when a scan finishes
              </Typography>
            )
          }>
          <div>
            <ControlActionButton
              disabled={disabled}
              label='Download PBOM'
              startIcon={<PBOMIcon />}
              endIcon={<ExpandMoreIcon />}
              classes={{ startIcon: classes.startIcon }}
            />
          </div>
        </Tooltip>
        {isScanning || disabled ? (
          <Tooltip
            arrow
            title={
              isScanning ? (
                <Typography variant='body2' color='text.primary'>
                  Option available when a scan finishes
                </Typography>
              ) : (
                <Typography variant='body2' color='text.primary'>
                  Option available when at least one application is selected
                </Typography>
              )
            }>
            <div>
              <ControlActionButton
                onClick={handleOwnersClick}
                disabled={disabled}
                label='Assign to'
                startIcon={<AccountCircleIcon />}
              />
            </div>
          </Tooltip>
        ) : (
          <div>
            <ControlActionButton
              onClick={handleOwnersClick}
              disabled={disabled}
              label='Assign to'
              startIcon={<AccountCircleIcon />}
              endIcon={<ExpandMoreIcon />}
            />
          </div>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  startIcon: {
    '& > svg': {
      width: '1em',
      height: '1em',
    },
  },
}));

export interface ApplicationControlsProps {
  disabled?: boolean;
  searchValue: string;
  selectedCount: number;
  ecrConfigured: boolean;
  searchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearSelection: () => void;
  handleOwnersClick: () => void;
  handleIrrelevantClick: () => void;
  handleEditPriorityClick: () => void;
  downloadBirthCertificate: (type: BirthCertificateResponseType) => void;
  handlePolicySettingsClick: () => void;
  handleOpenMenuSBOM: (e: SyntheticEvent) => void;
  handleCloseMenuSBOM: () => void;
  openSBOM: boolean;
}

export default ApplicationControls;
